// Integrated Coach Master Systems - Single Voice Approach
// Replaces lens-based system with unified coaching persona

interface CoachStrength {
  coachId: string;
  name: string;
  coreStrength: string;
  description: string;
}

// Core persona strengths for Integrated Coach blending
const coachStrengths: Record<string, CoachStrength> = {
  arjun: {
    coachId: 'arjun',
    name: 'Arjun',
    coreStrength: 'Emotional safety, deep listening',
    description: 'Provides psychological safety and emotional acknowledgment'
  },
  rajesh: {
    coachId: 'rajesh', 
    name: 'Rajesh',
    coreStrength: 'Decision clarity, ownership',
    description: 'Brings strategic thinking and leadership perspective'
  },
  samuel: {
    coachId: 'samuel',
    name: 'Samuel', 
    coreStrength: 'Practical action, problem-solving',
    description: 'Focuses on execution and tangible results'
  },
  priya: {
    coachId: 'priya',
    name: 'Priya',
    coreStrength: 'Career direction, growth paths', 
    description: 'Guides career strategy and professional development'
  },
  meera: {
    coachId: 'meera',
    name: 'Meera',
    coreStrength: 'Navigating change',
    description: 'Specializes in transitions and organizational dynamics'
  },
  aisha: {
    coachId: 'aisha',
    name: 'Aisha',
    coreStrength: 'Balance, sustainability',
    description: 'Ensures sustainable success and life integration'
  }
};

// Integrated Coach System - Single Voice Approach
function createIntegratedCoachProfile(activeCoachIds: string[]) {
  if (activeCoachIds.length <= 1) {
    return null;
  }

  const activeStrengths = activeCoachIds
    .map(id => coachStrengths[id])
    .filter(Boolean);

  return {
    name: 'Integrated Coach',
    strengths: activeStrengths,
    coachIds: activeCoachIds,
    openingStatement: 'Hi, I\'m your Integrated Coach.\n\nWhat would you like to be coached on today?'
  };
}

function getIntegratedCoachName(activeCoachIds: string[]): string {
  if (activeCoachIds.length <= 1) {
    return 'Coach';
  }
  
  // Always return "Integrated Coach" for consistency
  return 'Integrated Coach';
}

function getIntegratedCoachDescriptor(activeCoachIds: string[]): string {
  if (activeCoachIds.length <= 1) {
    return 'Single coach perspective.';
  }
  
  const activeStrengths = activeCoachIds
    .map(id => coachStrengths[id]?.coreStrength)
    .filter(Boolean);
    
  if (activeStrengths.length === 0) {
    return 'Integrated coaching perspective combining multiple approaches.';
  }
  
  return `Blends ${activeStrengths.join(', ')} into one natural, experienced voice.`;
}

export interface CombinedCoachProfile {
  id: string;
  name: string;
  descriptor: string;
  frameworks: string;
  tone: string;
  activeCoaches: string[];
}

/**
 * Creates an integrated coach profile from selected coach IDs using single voice approach
 */
export function createCombinedCoachProfile(selectedCoachIds: string[]): CombinedCoachProfile | null {
  if (selectedCoachIds.length <= 1) {
    return null;
  }

  const name = getIntegratedCoachName(selectedCoachIds);
  const descriptor = getIntegratedCoachDescriptor(selectedCoachIds);
  const profileKey = selectedCoachIds.sort().join('+');

  // Generate frameworks description based on integrated strengths
  const activeStrengths = selectedCoachIds
    .map(id => coachStrengths[id]?.coreStrength)
    .filter(Boolean);
    
  const frameworks = activeStrengths.length > 0 
    ? `Integrated approach: ${activeStrengths.join(' + ')}`
    : 'Multi-perspective coaching approach';

  // Single unified tone for Integrated Coach
  const tone = 'Calm, experienced professional voice that blends selected coach strengths naturally';

  return {
    id: profileKey,
    name,
    descriptor,
    frameworks,
    tone,
    activeCoaches: selectedCoachIds
  };
}

/**
 * Checks if a coach combination can create an integrated profile
 */
export function hasCombinedCoachProfile(selectedCoachIds: string[]): boolean {
  if (selectedCoachIds.length <= 1) {
    return false;
  }
  
  // Any combination of 2+ coaches can create an integrated profile
  return selectedCoachIds.length >= 2 && selectedCoachIds.length <= 3;
}

/**
 * Gets common integrated coach combinations
 */
export function getAllCombinedCoachProfiles(): CombinedCoachProfile[] {
  // Common effective combinations for integrated coaching
  const commonCombinations = [
    ['rajesh', 'samuel'], // Leadership + Performance
    ['priya', 'samuel'], // Career + Performance  
    ['meera', 'aisha'], // Transition + Life Balance
    ['arjun', 'samuel'], // EmpathEAR + Performance
    ['rajesh', 'priya', 'samuel'], // Leadership + Career + Performance
    ['arjun', 'aisha', 'priya'] // EmpathEAR + Life + Career
  ];

  return commonCombinations
    .map(coaches => createCombinedCoachProfile(coaches))
    .filter(Boolean) as CombinedCoachProfile[];
}
